/**
 * renderManual -- tiny self-contained markdown -> HTML renderer.
 *
 * Supports the subset actually used in dm-backend/docs/user_manual.md:
 *
 *   #/##/### headings        ->  <h1>/<h2>/<h3 id="slug">
 *   paragraphs                ->  <p>
 *   - / 1. lists              ->  <ul>/<ol><li>
 *   tables (GFM pipe)         ->  <table><thead><tbody>
 *   ```fenced code blocks```  ->  <pre><code>
 *   `inline code`             ->  <code>
 *   **bold**, *italic*        ->  <strong>/<em>
 *   [text](href)              ->  <a href> (target=_blank for absolute URLs)
 *   > blockquotes             ->  <blockquote>
 *   ---                       ->  <hr>
 *
 * Output is escaped before any markdown substitution so it is safe to
 * inject via dangerouslySetInnerHTML. We deliberately avoid any external
 * markdown library so the DM frontend keeps its current node_modules
 * footprint and builds with the shared cm-frontend-builder image.
 */

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function slugify(s) {
  return String(s)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-');
}

function renderInline(text) {
  let s = escapeHtml(text);

  // Fenced inline code first so other replacements don't touch its contents.
  s = s.replace(/`([^`]+)`/g, (_, code) => `<code>${code}</code>`);

  // Links: [label](href)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, href) => {
    const isAbs = /^https?:\/\//i.test(href);
    const isAnchor = href.startsWith('#');
    const extra = isAbs ? ' target="_blank" rel="noreferrer"' : '';
    const safeHref = isAnchor ? href : (isAbs ? href : href);
    return `<a href="${safeHref}"${extra}>${label}</a>`;
  });

  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');

  return s;
}

function renderTable(rows) {
  if (rows.length < 2) return '';
  const headerCells = rows[0].map((c) => `<th>${renderInline(c.trim())}</th>`).join('');
  const bodyRows = rows.slice(2)
    .map((r) => `<tr>${r.map((c) => `<td>${renderInline(c.trim())}</td>`).join('')}</tr>`)
    .join('');
  return `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`;
}

function splitRow(line) {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|');
}

export default function renderManual(markdown) {
  if (!markdown) return '';

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') { i += 1; continue; }

    if (line.startsWith('```')) {
      const buf = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith('```')) {
        buf.push(escapeHtml(lines[i]));
        i += 1;
      }
      i += 1;
      out.push(`<pre><code>${buf.join('\n')}</code></pre>`);
      continue;
    }

    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const text = h[2].trim();
      const id = slugify(text);
      out.push(`<h${level} id="${id}">${renderInline(text)}</h${level}>`);
      i += 1;
      continue;
    }

    if (line.trim() === '---') {
      out.push('<hr />');
      i += 1;
      continue;
    }

    if (line.startsWith('>')) {
      const buf = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        buf.push(lines[i].replace(/^>\s?/, ''));
        i += 1;
      }
      out.push(`<blockquote>${renderInline(buf.join(' '))}</blockquote>`);
      continue;
    }

    if (line.trim().startsWith('|')) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      out.push(renderTable(rows));
      continue;
    }

    if (line.match(/^\s*[-*]\s+/)) {
      const buf = [];
      while (i < lines.length && lines[i].match(/^\s*[-*]\s+/)) {
        buf.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      out.push(`<ul>${buf.map((b) => `<li>${renderInline(b)}</li>`).join('')}</ul>`);
      continue;
    }

    if (line.match(/^\s*\d+\.\s+/)) {
      const buf = [];
      while (i < lines.length && lines[i].match(/^\s*\d+\.\s+/)) {
        buf.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i += 1;
      }
      out.push(`<ol>${buf.map((b) => `<li>${renderInline(b)}</li>`).join('')}</ol>`);
      continue;
    }

    const paraBuf = [];
    while (i < lines.length && lines[i].trim() !== ''
            && !lines[i].startsWith('#')
            && !lines[i].startsWith('```')
            && !lines[i].startsWith('>')
            && !lines[i].trim().startsWith('|')
            && !lines[i].match(/^\s*[-*]\s+/)
            && !lines[i].match(/^\s*\d+\.\s+/)
            && lines[i].trim() !== '---') {
      paraBuf.push(lines[i]);
      i += 1;
    }
    out.push(`<p>${renderInline(paraBuf.join(' '))}</p>`);
  }

  return out.join('\n');
}
